import React, { useState, useEffect, useCallback } from "react";
import {
  Typography, Box, Paper, List, ListItem, ListItemText, IconButton, Button,
  Dialog, DialogTitle, DialogContent, TextField, DialogActions, MenuItem, Snackbar,
  Select, Checkbox, ListItemIcon, ListItemButton,
  FormControl, InputLabel
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  fetchUsersByCustomer, createUser, deleteUser, resetUserPassword,
  createPlayer, fetchPlayersByCustomer, updateUser,
  User, AssignedPlayer // NEU: User und AssignedPlayer Typen importieren
} from "../../api/api"; // User-Typ jetzt aus api.ts importieren
import { useTranslation } from "react-i18next";

interface UserManagementProps {
  customerId: string;
  onPlayerCreated: () => void;
}

// LOKALEN User-Typ ENTFERNT, da er jetzt aus api.ts importiert wird
// type User = {
//   id: number;
//   username: string;
//   role: "admin" | "user";
//   assigned_players: AssignedPlayer[];
// };

type Player = {
  sn: string;
  location: string;
};

const UserManagement: React.FC<UserManagementProps> = ({ customerId, onPlayerCreated }) => {
  const { t } = useTranslation();

  const [users, setUsers] = useState<User[]>([]); // Typ auf importierten User[] setzen
  const [allPlayersForCustomer, setAllPlayersForCustomer] = useState<Player[]>([]);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // States für Neuen Benutzer Dialog
  const [newUserDialog, setNewUserDialog] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newRole, setNewRole] = useState<"user" | "admin">("user");
  const [newAssignedPlayersSNs, setNewAssignedPlayersSNs] = useState<string[]>([]);

  // States für Benutzer Löschen Bestätigungsdialog
  const [confirmDeleteUserDialogOpen, setConfirmDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);

  // States für Neuen Player Dialog
  const [newPlayerDialogOpen, setNewPlayerDialogOpen] = useState(false);
  const [newPlayerSN, setNewPlayerSN] = useState("");
  const [newPlayerLocation, setNewPlayerLocation] = useState("");

  // NEU: States für Benutzer Bearbeiten Dialog
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [editUsername, setEditUsername] = useState("");
  const [editRole, setEditRole] = useState<"user" | "admin">("user");
  const [editAssignedPlayersSNs, setEditAssignedPlayersSNs] = useState<string[]>([]);
  const [editPassword, setEditPassword] = useState("");

  const reloadUsers = useCallback(() => {
    fetchUsersByCustomer(customerId).then(setUsers);
  }, [customerId]);

  const reloadPlayersForCustomer = useCallback(() => {
    fetchPlayersByCustomer(customerId)
      .then(setAllPlayersForCustomer)
      .catch(error => console.error("Fehler beim Laden der Player für den Kunden:", error));
  }, [customerId]);

  useEffect(() => {
    reloadUsers();
    reloadPlayersForCustomer();
  }, [customerId, reloadUsers, reloadPlayersForCustomer]);

  const handleCreateUser = async () => {
    try {
      await createUser(customerId, newUsername, "startPasswort123", newRole, newAssignedPlayersSNs);
      setNewUserDialog(false);
      setNewUsername("");
      setNewRole("user");
      setNewAssignedPlayersSNs([]);
      setSnackbar(t("userManagement.userCreated"));
      reloadUsers();
    } catch (error: any) {
      if (error && error.error === "duplicate_username") {
        setSnackbar(t("userManagement.duplicateUsernameError"));
      } else {
        setSnackbar(t("common.createUserError"));
        console.error("Error creating user:", error);
      }
    }
  };

  const handleDeleteUserClick = (id: number) => {
    setUserToDelete(id);
    setConfirmDeleteUserDialogOpen(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (userToDelete) {
      try {
        await deleteUser(customerId, userToDelete);
        setSnackbar(t("userManagement.userDeleted"));
        reloadUsers();
      } catch (error: any) {
        setSnackbar(t("common.deleteUserError"));
        console.error("Error deleting user:", error);
      }
    }
    setConfirmDeleteUserDialogOpen(false);
    setUserToDelete(null);
  };

  const handleCancelDeleteUser = () => {
    setConfirmDeleteUserDialogOpen(false);
    setUserToDelete(null);
  };

  const handleResetPassword = async (id: number) => {
    try {
      await resetUserPassword(customerId, id, "neuesPasswort123");
      setSnackbar(t("userManagement.passwordReset"));
    } catch (error: any) {
      setSnackbar(t("common.resetPasswordError"));
      console.error("Error resetting password:", error);
    }
  };

  const handleCreatePlayer = async () => {
    try {
      if (!newPlayerSN.trim() || !newPlayerLocation.trim()) {
        setSnackbar(t("playerManagement.playerFieldsRequired"));
        return;
      }
      await createPlayer(customerId, newPlayerSN.trim(), newPlayerLocation.trim());
      setNewPlayerDialogOpen(false);
      setNewPlayerSN("");
      setNewPlayerLocation("");
      setSnackbar(t("playerManagement.playerCreated"));
      reloadPlayersForCustomer();
      onPlayerCreated();
    } catch (error: any) {
      if (error && error.error === "duplicate_player_sn") {
        setSnackbar(t("playerManagement.duplicatePlayerSNError"));
      } else {
        setSnackbar(t("common.createPlayerError"));
        console.error("Error creating player:", error);
      }
    }
  };

  const handleNewUserPlayerSelectChange = (event: any) => {
    const { target: { value } } = event;
    setNewAssignedPlayersSNs(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleEditUserClick = (user: User) => {
    setUserToEdit(user);
    setEditUsername(user.username);
    setEditRole(user.role as "user" | "admin");
    // Sicherstellen, dass assigned_players ein Array ist, bevor map aufgerufen wird
    setEditAssignedPlayersSNs(user.assigned_players?.map(p => p.sn) || []);
    setEditPassword("");
    setEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!userToEdit) return;

    const updates: { username?: string; role?: string; password?: string; assignedPlayers?: string[] } = {};

    if (editUsername !== userToEdit.username) {
      updates.username = editUsername;
    }
    if (editRole !== userToEdit.role) {
      updates.role = editRole;
    }
    if (editPassword.trim() !== "") {
      updates.password = editPassword;
    }

    // Player-Zuweisungen immer senden, da sie komplett überschrieben werden
    // Konvertiere editAssignedPlayersSNs zu einem Set für einfachen Vergleich
    // Sicherstellen, dass assigned_players ein Array ist
    const currentAssignedSet = new Set(userToEdit.assigned_players?.map(p => p.sn) || []);
    const newAssignedSet = new Set(editAssignedPlayersSNs);

    // Prüfen, ob sich die zugewiesenen Player geändert haben
    const playersChanged =
      editAssignedPlayersSNs.length !== (userToEdit.assigned_players?.length || 0) ||
      ![...newAssignedSet].every(sn => currentAssignedSet.has(sn));

    if (playersChanged) {
      updates.assignedPlayers = editAssignedPlayersSNs;
    }

    if (Object.keys(updates).length === 0) {
      setSnackbar(t("userManagement.noChangesMade"));
      setEditUserDialog(false);
      return;
    }

    try {
      await updateUser(customerId, userToEdit.id, updates);
      setSnackbar(t("userManagement.userUpdated"));
      setEditUserDialog(false);
      reloadUsers();
    } catch (error: any) {
      if (error && error.error === "duplicate_username") {
        setSnackbar(t("userManagement.duplicateUsernameError"));
      } else {
        setSnackbar(t("common.updateUserError"));
        console.error("Error updating user:", error);
      }
    }
  };

  const handleEditUserPlayerSelectChange = (event: any) => {
    const { target: { value } } = event;
    setEditAssignedPlayersSNs(
      typeof value === 'string' ? value.split(',') : value,
    );
  };


  return (
    <Paper sx={{ p: 1.5, mt: 1.5 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6">{t("userManagement.usersHeading")}</Typography>
        <Box>
          <Button variant="outlined" onClick={() => setNewPlayerDialogOpen(true)} sx={{ mr: 1 }}>
            <AddIcon sx={{ mr: 0.5 }} /> {t("playerManagement.createPlayerButton")}
          </Button>
          <Button variant="outlined" onClick={() => {
            setNewUserDialog(true);
            setNewUsername("");
            setNewRole("user");
            setNewAssignedPlayersSNs([]);
          }}>
            {t("userManagement.createUserButton")}
          </Button>
        </Box>
      </Box>
      <List>
        {users.map((user) => (
          <ListItem key={user.id}
            secondaryAction={
              <Box>
                <IconButton onClick={() => handleResetPassword(user.id)} title={t("userManagement.resetPasswordTitle")}>
                  <RestartAltIcon />
                </IconButton>
                <IconButton onClick={() => handleEditUserClick(user)} title={t("common.edit")}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDeleteUserClick(user.id)} title={t("common.delete")} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            }>
            <ListItemText
              primary={user.username}
              secondary={
                <>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t("userManagement.userRoleDisplay", { role: user.role })}
                  </Typography>
                  {/* KORRIGIERT: JSX-Kommentar entfernt */}
                  {user.assigned_players && user.assigned_players.length > 0 && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                      {t("userManagement.assignedPlayersDisplay")}{" "}
                      {user.assigned_players.map(p => p.sn).join(', ')}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        ))}
        {users.length === 0 && (
          <ListItem>
            <ListItemText primary={t("userManagement.noUsers")} />
          </ListItem>
        )}
      </List>

      <Dialog open={newUserDialog} onClose={() => setNewUserDialog(false)}>
        <DialogTitle>{t("userManagement.createUserDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("userManagement.usernameLabel")}
            value={newUsername}
            onChange={e => setNewUsername(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label={t("userManagement.roleLabel")}
            value={newRole}
            onChange={e => setNewRole(e.target.value as "user" | "admin")}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="user">{t("userManagement.userRole")}</MenuItem>
            <MenuItem value="admin">{t("userManagement.adminRole")}</MenuItem>
          </TextField>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="assign-players-label">{t("userManagement.assignPlayersLabel")}</InputLabel>
            <Select
              labelId="assign-players-label"
              multiple
              value={newAssignedPlayersSNs}
              onChange={handleNewUserPlayerSelectChange}
              renderValue={(selected) => selected.join(', ')}
              label={t("userManagement.assignPlayersLabel")}
            >
              {allPlayersForCustomer.map((player) => (
                <MenuItem key={player.sn} value={player.sn}>
                  <ListItemIcon>
                    <Checkbox checked={newAssignedPlayersSNs.indexOf(player.sn) > -1} />
                  </ListItemIcon>
                  <ListItemText primary={`${player.sn} (${player.location})`} />
                </MenuItem>
              ))}
              {allPlayersForCustomer.length === 0 && (
                <MenuItem disabled>
                  <ListItemText primary={t("userManagement.noPlayersToAssign")} />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewUserDialog(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreateUser} disabled={!newUsername}>
            {t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editUserDialog} onClose={() => setEditUserDialog(false)}>
        <DialogTitle>{t("userManagement.editUserDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("userManagement.usernameLabel")}
            value={editUsername}
            onChange={e => setEditUsername(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label={t("userManagement.roleLabel")}
            value={editRole}
            onChange={e => setEditRole(e.target.value as "user" | "admin")}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="user">{t("userManagement.userRole")}</MenuItem>
            <MenuItem value="admin">{t("userManagement.adminRole")}</MenuItem>
          </TextField>
          <TextField
            label={t("userManagement.passwordLabel")}
            type="password"
            value={editPassword}
            onChange={e => setEditPassword(e.target.value)}
            fullWidth
            placeholder={t("userManagement.passwordPlaceholder")}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="edit-assign-players-label">{t("userManagement.assignPlayersLabel")}</InputLabel>
            <Select
              labelId="edit-assign-players-label"
              multiple
              value={editAssignedPlayersSNs}
              onChange={handleEditUserPlayerSelectChange}
              renderValue={(selected) => selected.join(', ')}
              label={t("userManagement.assignPlayersLabel")}
            >
              {allPlayersForCustomer.map((player) => (
                <MenuItem key={player.sn} value={player.sn}>
                  <ListItemIcon>
                    <Checkbox checked={editAssignedPlayersSNs.indexOf(player.sn) > -1} />
                  </ListItemIcon>
                  <ListItemText primary={`${player.sn} (${player.location})`} />
                </MenuItem>
              ))}
              {allPlayersForCustomer.length === 0 && (
                <MenuItem disabled>
                  <ListItemText primary={t("userManagement.noPlayersToAssign")} />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleUpdateUser} disabled={!editUsername}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newPlayerDialogOpen} onClose={() => setNewPlayerDialogOpen(false)}>
        <DialogTitle>{t("playerManagement.createPlayerDialogTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label={t("playerManagement.playerSNLabel")}
            value={newPlayerSN}
            onChange={(e) => setNewPlayerSN(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t("playerManagement.playerLocationLabel")}
            value={newPlayerLocation}
            onChange={(e) => setNewPlayerLocation(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewPlayerDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCreatePlayer} disabled={!newPlayerSN.trim() || !newPlayerLocation.trim()}>
            {t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteUserDialogOpen}
        onClose={handleCancelDeleteUser}
        aria-labelledby="confirm-delete-user-title"
        aria-describedby="confirm-delete-user-description"
      >
        <DialogTitle id="confirm-delete-user-title">{t("userManagement.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography id="confirm-delete-user-description">
            {t("userManagement.confirmDeleteMessage", { userName: users.find(u => u.id === userToDelete)?.username })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteUser} color="primary">{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteUser} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={1800}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Paper>
  );
};

export default UserManagement;